

/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react-native/no-inline-styles */
/**
 * Splash Screen
 * 
 * Initial loading screen that checks authentication status
 * - Verifies access/refresh tokens from storage
 * - If authenticated: fetches profile, company info, payment methods → navigates to Home
 * - If not authenticated: navigates to Login
 * - Shows error message with retry button on failure
 * - Includes Android back button handler
 * 
 * @component
 * @param {Object} navigation - React Navigation object
 * @param {Object} route - Route params
 */
import React, { useState, useEffect } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ActivityIndicator, Button, Text } from 'react-native-paper';
import { theme } from '../theme/theme';
import * as storage from '../services/storage';
import navigationService from '../services/NavigationService';
import { errorHandler, getCompanyInfo, getErrorMsg, getPaymentGateways, getProfile } from '../services/Utils';
import { useIsFocused } from '@react-navigation/native';
import BackButton from '../components/BackButton';

function Splash({ navigation, route }) {
    const isFocused = useIsFocused();
    const showBackButton = isFocused && Platform.OS === 'android';
    const [_error, setError] = useState(false);
    const [message, setMessage] = useState('');

    /**
     * Checks authentication tokens and fetches user data
     * Navigates to appropriate screen based on auth status
     */
    const getToken = async () => {
        setError(false);
        try {
            navigationService.navigation = navigation;
            const accessToken = await storage.getData('accessToken');
            if (accessToken) {
                await getProfile();
                await getCompanyInfo();
                await getPaymentGateways();
                navigation.replace('Home');
            } else {
                navigation.replace('Login');
            }
        } catch (error) {
            console.error(error);
            setError(true);
            if (error?.message === 'Network Error') {
                setMessage('Network Error. Please try again later');
            } else {
                setMessage(getErrorMsg(error));
            }
            errorHandler(error);
        }
    };

    useEffect(() => {
        getToken();
    }, [isFocused]);

    return (
        <SafeAreaView style={styles.container}>
            {!_error ?
                <React.Fragment>
                    <ActivityIndicator color="#ffffff" size={'large'} />
                    <Text style={{ padding: 16, color: '#fff' }} variant="labelLarge">Please wait loading....</Text>
                </React.Fragment>
                : (
                    <React.Fragment>
                        <Text style={{ padding: 16, color: '#fff' }} variant="labelLarge">{message}</Text>
                        <Button mode="elevated" onPress={getToken}>
                            Retry
                        </Button>
                    </React.Fragment>
                )
            }
            {showBackButton && <BackButton isRoot={true} />}
        </SafeAreaView >
    );
}

// 🔹 Styles
const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.primary,
        justifyContent: 'center',
        alignItems: 'center',
    },
});

export default Splash;
